import { apiClient } from './client';

/**
 * Meldet der Mitarbeiterverwaltung, dass der angemeldete Mensch gerade in PHQ ist.
 *
 * Hintergrund: PHQ hat keinen eigenen Speicher. Damit in der Benutzerverwaltung
 * stehen kann, wann jemand zuletzt hier war, muss der Zeitpunkt in einem Dienst
 * liegen — und er muss VON PHQ kommen, sonst misst er etwas anderes. Das BFF sieht
 * alle Anfragen des Verbunds; ein Zeitstempel von dort hiesse „war irgendwo aktiv".
 *
 * Die Route trägt kein Ziel: kein `:id`, kein Rumpf. Wer gemeint ist, leitet die
 * Mitarbeiterverwaltung aus dem geprüften Token ab. Deshalb braucht der Aufruf auch
 * kein Verwaltungsrecht — es gibt nichts zu wählen und damit nichts zu autorisieren.
 */
const ROUTE = '/api/v1/employee-api/api/v1/employees/self/last-seen';

/** Schlüssel im Sitzungsspeicher für die Drosselung. */
const SCHLUESSEL = 'pm_anwesenheit_gemeldet';

/**
 * Mindestabstand zwischen zwei Meldungen.
 *
 * 🔴 Die Drosselung ist kein Feinschliff. Ohne sie hinge an jedem Seitenwechsel ein
 * Schreibvorgang in einem fremden Dienst, auf einem Weg, der heute nur liest. Für die
 * Frage „wann zuletzt in PHQ" genügt eine Auflösung von Minuten — niemand liest in
 * einer Tabelle ab, ob jemand um 9:14 oder 9:17 da war.
 */
const ABSTAND_MS = 5 * 60 * 1000;

/** Antwort der Route: Sie sagt ausdrücklich, ob geschrieben wurde — und warum nicht. */
interface AnwesenheitsAntwort {
  geschrieben?: boolean;
  grund?: string | null;
}

/** Damit eine Absage einmal je Sitzung auffällt und nicht bei jedem Aufruf lärmt. */
let absageGemeldet = false;

/**
 * Meldet die Anwesenheit, höchstens einmal je Zeitfenster.
 *
 * Scheitert bewusst still: Der Aufruf ist eine Nebensache. Läuft die Route noch nicht
 * (404) oder ist der Dienst gerade nicht erreichbar, darf das die Oberfläche in keiner
 * Weise stören — der Mensch wollte arbeiten, nicht gezählt werden.
 *
 * 🔴 Eine HÖFLICHE ABSAGE ist aber etwas anderes als ein Fehler, und sie ist der
 * gefährlichere Fall: Die Route antwortet mit HTTP 200 und
 * `{ geschrieben: false, grund: "nicht_zuordenbar" }`, wenn sie den Mitarbeitersatz
 * nicht findet — etwa weil die Adresse im Anmeldetoken nicht buchstabengleich der
 * hinterlegten ist, oder weil zwei Sätze dieselbe Adresse tragen (`mehrdeutig`).
 * Wer nur auf den Statuscode oder auf eine Ausnahme prüft, hält das für Erfolg, und
 * die Spalte bleibt leer, ohne dass irgendwo etwas auffällt (Hinweis employee,
 * 22.09.2026). Deshalb wird der Grund ausgewertet und einmal je Sitzung gemeldet.
 */
export async function meldeAnwesenheit(): Promise<void> {
  try {
    const zuletzt = Number(sessionStorage.getItem(SCHLUESSEL) ?? '0');
    if (Number.isFinite(zuletzt) && Date.now() - zuletzt < ABSTAND_MS) return;
    // Der Zeitstempel wird VOR dem Aufruf gesetzt: Scheitert er, wird nicht bei jedem
    // Klick erneut versucht. Ein nicht gemeldeter Besuch ist harmloser als eine
    // Schleife gegen einen Dienst, der gerade nicht kann.
    sessionStorage.setItem(SCHLUESSEL, String(Date.now()));
    const antwort = (await apiClient.post(ROUTE, {})) as { data?: AnwesenheitsAntwort } & AnwesenheitsAntwort;
    const nutz = antwort?.data ?? antwort;
    if (nutz && nutz.geschrieben === false && !absageGemeldet) {
      absageGemeldet = true;
      console.warn(
        '[Anwesenheit] Der Mitarbeiterdienst hat den Besuch NICHT gespeichert. ' +
        `Grund: ${nutz.grund ?? 'unbekannt'}. ` +
        'Die Spalte „Zuletzt in PHQ" bleibt deshalb leer — das ist kein Anzeigefehler.',
      );
    }
  } catch {
    // bewusst leer — siehe oben
  }
}
